<template>
  <div id="modview" ref="carouselContainer" class="carousel slide">
    <div ref="carouselInner" class="carousel-inner" data-cy="modview-wrapper">
      <template v-if="items.length > 0">
        <div
          v-for="(page, pageIndex) in paginatedItems"
          :key="pageIndex"
          class="carousel-item"
          :class="{ active: pageIndex === 0 }"
        >
          <ul class="modview__grid-page h-100">
            <li
              v-for="(item, itemIndex) in page"
              :key="`${item.key}__${itemIndex}`"
              class="d-block"
            >
              <landing-page-modview-grid-mod-item
                :item="item"
                @select-mod="onModSelected"
              />
            </li>
          </ul>
        </div>
      </template>
      <p v-else class="text-center">{{ t('landingpage/mods/not_found') }}</p>
    </div>

    <template v-if="paginatedItems.length > 0">
      <button
        v-if="currentPage > 0"
        class="btn btn-dark d-none d-md-block position-absolute top-50 start-0 translate-middle py-4 px-3"
        data-bs-slide="prev"
        data-bs-target="#modview"
        type="button"
      >
        <fa-icon class="text-rhino" :icon="faChevronLeft" />
      </button>
      <button
        v-if="currentPage < paginatedItems.length - 1"
        class="btn btn-dark d-none d-md-block position-absolute top-50 start-100 translate-middle py-4 px-3"
        data-bs-slide="next"
        data-bs-target="#modview"
        type="button"
      >
        <fa-icon class="text-rhino" :icon="faChevronRight" />
      </button>
    </template>
  </div>
</template>

<script lang="ts">
import type { Carousel } from 'bootstrap';
import type { StrapiMinecraftModpack } from '~/apollo/types/types';
import useBootstrapCarousel from '~/composables/bootstrap/useCarousel';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

export const INITIAL_BATCH_SIZE = 36;
export const PAGINATION_BATCH_SIZE = 18;

export default defineComponent({
  props: {
    items: {
      type: Array as PropType<Array<StrapiMinecraftModpack>>,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 4,
    },
    lazyLoadingOffset: {
      type: Number,
      default: 2,
    },
    isResetting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['invoke-pagination', 'select-mod'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const carouselContainer = ref<HTMLElement | null>(null);
    const carouselInner = ref<HTMLElement | null>(null);
    const currentPage = ref<number>(0);

    const sliceIntoChunks = (arr, chunkSize) => {
      if (chunkSize === 0) return [];

      const res = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        res.push(chunk);
      }

      return res;
    };

    const paginatedItems = computed<Array<Array<StrapiMinecraftModpack>>>(() =>
      sliceIntoChunks(props.items, props.itemsPerPage),
    );

    // desktop pagination
    const handleBsCarouselSlide = (e) => {
      const { to, from } = e as unknown as Carousel.Event;
      currentPage.value = to;

      if (
        to > 0 &&
        // the offset is set to 2, so we want to load the next batch when we are 2 pages away from the end
        currentPage.value ===
          paginatedItems.value.length - props.lazyLoadingOffset &&
        // if we navigate back we do not want to load any more items
        to > from
      ) {
        emit('invoke-pagination', {
          batchSize: PAGINATION_BATCH_SIZE,
        });
      }
    };

    const { carouselInstance, instanceAvailable } = useBootstrapCarousel(
      carouselContainer,
      {
        interval: false,
        wrap: false,
        touch: false,
      },
      handleBsCarouselSlide,
    );

    // if the resetting state is true we want to reset the pagination
    watch(
      () => props.isResetting,
      (value) => {
        if (value && instanceAvailable.value) {
          currentPage.value = 0;
          carouselInstance.value.to(0);
        }
      },
    );

    // mobile pagination
    const { smaller } = useAppBreakpoints();
    const isSmallerMd = smaller('md');
    const { arrivedState, x } = useScroll(carouselInner, {
      offset: { right: process.client ? window.innerWidth * 2 : 0 },
    });

    watch(
      () => arrivedState.right && isSmallerMd.value,
      (value) => {
        if (value) {
          emit('invoke-pagination', {
            batchSize: PAGINATION_BATCH_SIZE,
          });
        }
      },
    );

    const onModSelected = (item) => {
      emit('select-mod', item);
    };

    const resetPosition = () => {
      currentPage.value = 0;
      carouselInstance.value.to(0);
      x.value = 0;
    };

    return {
      faChevronLeft,
      faChevronRight,

      t,
      emit,
      onModSelected,
      paginatedItems,
      currentPage,
      carouselContainer,
      carouselInner,
      resetPosition,
    };
  },
});
</script>
<style lang="scss" scoped>
@import 'bootstrap/scss/mixins/breakpoints';
@import 'gportal-theme/scss/breakpoints.scss';

.modview__grid {
  &-page {
    display: grid;
    gap: 0.5rem;
    grid-template-rows: 1fr 1fr;
    grid-auto-flow: column;

    @include media-breakpoint-up(md) {
      grid-auto-flow: row;
      grid-template-rows: repeat(3, 1fr);
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &-item {
    width: 300px;
    scroll-snap-align: start;

    @include media-breakpoint-up(sm) {
      width: 400px;
    }

    @include media-breakpoint-up(md) {
      width: auto;
      scroll-snap-align: unset;
    }
  }
}

.carousel {
  &-inner {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;

    @include media-breakpoint-up(md) {
      display: block;
      overflow: hidden;
      scroll-snap-type: unset;
    }
  }

  &-item {
    display: block;
    width: auto;
    margin-right: 0.5rem;

    @include media-breakpoint-up(md) {
      width: 100%;
      display: none;
      margin-right: -100%;

      &.active {
        display: block;
      }

      &-prev,
      &-next {
        display: block;
      }
    }
  }
}

button.start-0 {
  margin-left: 0.5rem;
}

button.start-100 {
  margin-left: -0.5rem;
}
</style>
